<template>
  <main
    class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-28"
  >
    <section class="mx-auto max-w-4xl pb-56">
      <h2 class="text-gray-900 text-4xl font-medium">
        Welcome {{ user?.fullName }}!
      </h2>
      <p class="text-gray-600 text-sm mt-2">
        Your network, updates, and learning in one place.
      </p>
      <div
        v-if="!isCommunityPlus"
        class="mt-6 bg-white border border-gray-200 shadow-sm p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
      >
        <div>
          <h3 class="text-gray-900 text-lg font-semibold flex items-center gap-2">
            <SparklesIcon class="h-5 w-5 text-primary" />
            Upgrade to Community Plus
          </h3>
          <p class="text-sm text-gray-600 mt-1">
            Unlock the Academy and Member Directory with Community Plus.
          </p>
        </div>
        <router-link
          class="inline-flex items-center justify-center rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100"
          to="/dashboard/account"
        >
          Upgrade
        </router-link>
      </div>
      <div
        v-if="isCommunityPlus"
        class="grid gap-4 mt-6 md:grid-cols-3"
      >
        <div class="bg-white border border-gray-200 shadow-sm rounded-2xl p-4">
          <div class="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
            <UsersIcon class="h-4 w-4 text-gray-400" />
            Total Members
          </div>
          <div class="text-2xl font-semibold text-gray-900 mt-1">
            {{ users.length }}
          </div>
        </div>
        <div class="bg-white border border-gray-200 shadow-sm rounded-2xl p-4">
          <div class="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
            <CalendarDaysIcon class="h-4 w-4 text-gray-400" />
            Upcoming Events
          </div>
          <div class="text-2xl font-semibold text-gray-900 mt-1">
            {{ upcomingEvents.length }}
          </div>
        </div>
        <div class="bg-white border border-gray-200 shadow-sm rounded-2xl p-4">
          <div class="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500">
            <AcademicCapIcon class="h-4 w-4 text-gray-400" />
            Courses Available
          </div>
          <div class="text-2xl font-semibold text-gray-900 mt-1">
            {{ courses.length }}
          </div>
        </div>
      </div>
      <div
        v-if="showSetup"
        class="mt-10 bg-white border border-gray-200 shadow-sm p-6"
      >
        <h3 class="text-gray-900 text-2xl font-semibold">
          Setup Account
        </h3>
        <p class="text-gray-600 mt-2">
          To get started, please set up your account by providing all the required information.
        </p>
        <form
          class="mt-6 grid gap-6 md:grid-cols-2"
          @submit.prevent="saveSetup"
        >
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-3">
              Which VNTR offerings appeal to you? *
            </label>
            <div class="grid gap-3 md:grid-cols-2">
              <label
                v-for="option in offeringsOptions"
                :key="option"
                class="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  v-model="selectedOfferings"
                  class="h-4 w-4 rounded border-gray-300"
                  type="checkbox"
                  :value="option"
                >
                {{ option }}
              </label>
            </div>
          </div>
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-3">
              How would you describe yourself? *
            </label>
            <div class="flex flex-wrap gap-4">
              <label
                v-for="option in personaOptions"
                :key="option"
                class="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  v-model="persona"
                  class="h-4 w-4 border-gray-300"
                  type="radio"
                  :value="option"
                >
                {{ option }}
              </label>
            </div>
          </div>
          <div class="md:col-span-2 flex items-center gap-3">
            <button
              class="inline-flex items-center rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
              type="submit"
              :disabled="!canSaveSetup"
            >
              Save Setup
            </button>
            <span
              v-if="setupError"
              class="text-sm text-red-600"
            >
              {{ setupError }}
            </span>
          </div>
        </form>
      </div>
      <div class="grid gap-6 mt-10 md:grid-cols-2">
        <div
          class="bg-white border border-gray-200 shadow-sm rounded-2xl p-5"
        >
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-gray-900 text-lg font-medium flex items-center gap-2">
              <MegaphoneIcon class="h-5 w-5 text-primary" />
              Announcements
            </h3>
            <span class="text-xs text-gray-500">Latest</span>
          </div>
          <div
            v-if="announcements.length === 0"
            class="text-sm text-gray-500"
          >
            No announcements yet.
          </div>
          <ul
            v-else
            class="space-y-3 text-sm text-gray-700"
          >
            <li
              v-for="announcement in announcements.slice(0, 3)"
              :key="announcement._id"
              class="border-l-2 border-primary pl-3"
            >
              <router-link
                class="font-medium text-gray-900 hover:text-primary"
                :to="`/dashboard/announcements/${announcement._id}`"
              >
                {{ announcement.title }}
              </router-link>
              <div class="text-gray-600">
                {{ announcement.body }}
              </div>
              <router-link
                class="inline-flex items-center mt-2 text-xs font-medium text-gray-900 border border-gray-300 rounded-full px-3 py-1 hover:bg-gray-100"
                :to="`/dashboard/announcements/${announcement._id}`"
              >
                View
                <ArrowRightIcon class="ml-1 h-3 w-3" />
              </router-link>
            </li>
          </ul>
        </div>
        <div
          class="bg-white border border-gray-200 shadow-sm rounded-2xl p-5"
        >
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-gray-900 text-lg font-medium flex items-center gap-2">
              <CalendarDaysIcon class="h-5 w-5 text-primary" />
              Upcoming Events
            </h3>
            <router-link
              class="text-sm font-medium text-gray-900 inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1"
              to="/dashboard/events"
            >
              View all
              <ArrowRightIcon class="h-4 w-4 text-gray-900" />
            </router-link>
          </div>
          <div
            v-if="upcomingEvents.length === 0"
            class="text-sm text-gray-500"
          >
            No events scheduled yet.
          </div>
          <div
            v-else
            class="space-y-3"
          >
            <div
              v-for="event in upcomingEvents.slice(0, 3)"
              :key="event._id"
              class="border border-gray-200 rounded-2xl p-3"
            >
              <div>
                <router-link
                  class="text-sm font-medium text-gray-900 hover:text-primary"
                  :to="`/dashboard/events/${event._id}`"
                >
                  {{ event.title }}
                </router-link>
                <div class="mt-1 flex items-start justify-between gap-4">
                  <div>
                    <div class="text-xs text-gray-600">
                      {{ formatDate(event.date) }}
                      <span v-if="event.startTime || event.endTime">
                        • {{ formatTimeRange(event.startTime, event.endTime) }}
                      </span>
                    </div>
                    <div
                      v-if="event.location"
                      class="text-xs text-gray-600"
                    >
                      {{ event.location }}
                    </div>
                    <div class="text-xs text-gray-600">
                      <span v-if="event.hidePrice">Paid event</span>
                    </div>
                  </div>
                  <div class="text-right text-sm font-semibold text-gray-900">
                    {{ getEventPriceLabel(event) }}
                  </div>
                </div>
              </div>
              <router-link
                class="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-3 py-2 text-xs font-semibold text-gray-900 hover:bg-[#c9ad99]"
                :to="`/dashboard/events/${event._id}`"
              >
                <TicketIcon class="h-4 w-4" />
                See event
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="isCommunityPlus"
        class="grid gap-6 mt-10 md:grid-cols-2"
      >
        <div class="bg-white border border-gray-200 shadow-sm rounded-2xl p-5">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-gray-900 text-lg font-medium flex items-center gap-2">
              <UserGroupIcon class="h-5 w-5 text-primary" />
              Featured Members
            </h3>
            <router-link
              class="text-sm font-medium text-gray-900 inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1"
              to="/dashboard/directory"
            >
              View directory
              <ArrowRightIcon class="h-4 w-4 text-gray-900" />
            </router-link>
          </div>
          <div
            v-if="users.length === 0"
            class="text-sm text-gray-500"
          >
            No members found.
          </div>
          <div
            v-else
            class="space-y-3"
          >
            <div
              v-for="member in users.slice(0, 4)"
              :key="member._id"
              class="flex items-center justify-between"
            >
              <div class="flex items-center gap-3">
                <div class="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                  {{ getInitials(member.fullName || member.email) }}
                </div>
                <div>
                  <div class="text-sm font-medium text-gray-900">
                    {{ member.fullName || 'Member' }}
                  </div>
                  <div class="text-xs text-gray-600">
                    {{ member.company || member.email }}
                  </div>
                </div>
              </div>
              <button
                class="text-xs font-medium text-gray-900 border border-gray-300 rounded-full px-3 py-1 hover:bg-gray-100 inline-flex items-center gap-1"
                type="button"
                @click="toggleContact(member._id)"
              >
                <UserPlusIcon
                  v-if="!isContact(member._id)"
                  class="h-3.5 w-3.5"
                />
                <CheckIcon
                  v-else
                  class="h-3.5 w-3.5"
                />
                {{ isContact(member._id) ? 'Added' : 'Add' }}
              </button>
            </div>
          </div>
        </div>
        <div class="bg-white border border-gray-200 shadow-sm rounded-2xl p-5">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-gray-900 text-lg font-medium flex items-center gap-2">
              <BookOpenIcon class="h-5 w-5 text-primary" />
              Recommended Learning
            </h3>
            <router-link
              class="text-sm font-medium text-gray-900 inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1"
              to="/academy"
            >
              View courses
              <ArrowRightIcon class="h-4 w-4 text-gray-900" />
            </router-link>
          </div>
          <div
            v-if="courses.length === 0"
            class="text-sm text-gray-500"
          >
            No courses available.
          </div>
          <div
            v-else
            class="space-y-3"
          >
            <div
              v-for="course in courses.slice(0, 3)"
              :key="course._id"
              class="flex items-center justify-between border border-gray-200 rounded-2xl p-3"
            >
              <div>
                <div class="text-sm font-medium text-gray-900">
                  {{ course.title }}
                </div>
                <div class="text-xs text-gray-600">
                  {{ course.modules.length }} modules • {{ getTotalVideos(course) }} videos
                </div>
              </div>
              <router-link
                class="text-xs font-medium text-primary inline-flex items-center"
                :to="`/dashboard/course/${course._id}`"
              >
                Open
                <ArrowRightIcon class="ml-1 h-3.5 w-3.5" />
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import {computed, defineComponent, inject, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {userKey} from '@/composables/useUser';
import {Announcement, Course, Event, User} from '@/types';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import {
  AcademicCapIcon,
  ArrowRightIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  CheckIcon,
  MegaphoneIcon,
  SparklesIcon,
  TicketIcon,
  UserGroupIcon,
  UserPlusIcon,
  UsersIcon,
} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: "DashboardHome",
  components: {
    AcademicCapIcon,
    ArrowRightIcon,
    BookOpenIcon,
    CalendarDaysIcon,
    CheckIcon,
    MegaphoneIcon,
    SparklesIcon,
    TicketIcon,
    UserGroupIcon,
    UserPlusIcon,
    UsersIcon,
  },
  setup() {
    const {settings} = inject(settingsKey)!;

    const {user} = inject(userKey)!;

    const courses = ref<Course[]>([]);
    const users = ref<User[]>([]);
    const events = ref<Event[]>([]);
    const announcements = ref<Announcement[]>([]);
    const contactIds = ref<string[]>([]);
    const selectedOfferings = ref<string[]>([]);
    const persona = ref<string>('');
    const setupError = ref('');
    const loading = ref<boolean>(true);
    const error = ref<string | null>(null);

    const fetchCourses = async (): Promise<void> => {
      try {
        const response = await axios.get<Course[]>('/course/all');
        courses.value = response.data;
      } catch (err) {
        error.value = "An error occurred while loading the courses!";
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const fetchUsers = async (): Promise<void> => {
      try {
        const response = await axios.get<User[]>('/user/all');
        users.value = response.data;
      } catch (err) {
        console.error('Failed to load users:', err);
      }
    };

    const fetchEvents = async (): Promise<void> => {
      try {
        const response = await axios.get<Event[]>('/events');
        events.value = response.data;
      } catch (err) {
        console.error('Failed to load events:', err);
      }
    };

    const fetchAnnouncements = async (): Promise<void> => {
      try {
        const response = await axios.get<Announcement[]>('/announcements');
        announcements.value = response.data;
      } catch (err) {
        console.error('Failed to load announcements:', err);
      }
    };

    const getTotalVideos = (course: Course): number => {
      return course.modules.reduce((total, module) => total + module.contents.length, 0);
    };


    const formatDate = (value?: string): string => {
      if (!value) return 'Unknown date';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'Unknown date';
      return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(date);
    };

    const formatTimeRange = (start?: string, end?: string): string => {
      if (start && end) return `${start} to ${end}`;
      if (start) return `From ${start}`;
      if (end) return `Until ${end}`;
      return '';
    };

    const formatPrice = (value: number): string => {
      return value.toFixed(2);
    };

    const getEventPriceLabel = (event: Event): string => {
      if (event.hidePrice) return 'Paid';
      if (event.price == null) return 'Free';
      const numericPrice = Number(event.price);
      if (Number.isNaN(numericPrice)) return 'Free';
      return `£${formatPrice(numericPrice)}`;
    };

    const getInitials = (value: string): string => {
      const parts = value.trim().split(' ');
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    };

    const loadContacts = (): void => {
      if (typeof window === 'undefined') return;
      try {
        const stored = localStorage.getItem('nevnet-contacts');
        contactIds.value = stored ? JSON.parse(stored) : [];
      } catch (error) {
        console.error('Failed to load contacts:', error);
        contactIds.value = [];
      }
    };

    const saveContacts = (): void => {
      if (typeof window === 'undefined') return;
      localStorage.setItem('nevnet-contacts', JSON.stringify(contactIds.value));
    };

    const toggleContact = (userId: string): void => {
      if (contactIds.value.includes(userId)) {
        contactIds.value = contactIds.value.filter((id) => id !== userId);
      } else {
        contactIds.value = [...contactIds.value, userId];
      }
      saveContacts();
    };

    const offeringsOptions = [
      'Events for Investors',
      'Exclusive Deals',
      'Global Investor Community',
      'Curated VC Funds',
    ];

    const personaOptions = ['Investor', 'Sponsor', 'Startup'];

    const canSaveSetup = computed(() => selectedOfferings.value.length > 0 && Boolean(persona.value));

    const showSetup = computed(() => !user.value?.setupCompleted);

    const loadSetup = (): void => {
      selectedOfferings.value = user.value?.offerings ? [...user.value.offerings] : [];
      persona.value = user.value?.persona || '';
    };

    const saveSetup = async (): Promise<void> => {
      setupError.value = '';
      if (!user.value) return;
      try {
        const response = await axios.put<User>(`/user/update/${user.value._id}`, {
          offerings: selectedOfferings.value,
          persona: persona.value,
          setupCompleted: true,
        });
        user.value = response.data;
      } catch (err) {
        console.error('Failed to save setup:', err);
        setupError.value = 'Failed to save setup.';
      }
    };

    const upcomingEvents = computed(() => {
      const now = new Date().getTime();
      return events.value
        .filter((event) => {
          const date = new Date(event.date).getTime();
          return !Number.isNaN(date) && date >= now;
        })
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });

    onMounted(async () => {
      loadContacts();
      await Promise.all([fetchCourses(), fetchUsers(), fetchEvents(), fetchAnnouncements()]);
    });

    watchEffect(() => {
      if (user.value) {
        loadSetup();
      }
    });

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: 'Dashboard',
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    return {
      user,
      courses,
      users,
      events,
      announcements,
      upcomingEvents,
      isCommunityPlus: computed(() => user.value?.membershipPlan === 'community_plus'),
      loading,
      error,
      getTotalVideos,
      formatDate,
      formatTimeRange,
      formatPrice,
      getEventPriceLabel,
      getInitials,
      toggleContact,
      isContact: (userId: string) => contactIds.value.includes(userId),
      offeringsOptions,
      personaOptions,
      selectedOfferings,
      persona,
      canSaveSetup,
      showSetup,
      saveSetup,
      setupError,
    };
  },
});
</script>
